import { useMemo, useState } from 'react';

import { Collapse } from '@mui/material';

import { CAST_AI } from '@cast/constants';
import { Icons, Stack } from '@cast/design-system';
import { InstallableKentFeature } from '@cast/types-kent';

import {
  AgentSetupBlock,
  FeatureLabel,
} from 'hooks/dialogs/cluster/components/features';

import { KentFeatureItem } from './KentFeatureItem';
import { ToggleButton } from './ToggleButton';
import { getKentFeaturesMap } from '../../../_utils';
import {
  isAtLeastOneKentFeatureInstalled,
  isKentFeatureInstalled,
} from '../../../utils';
import { SelectedFeatures } from '../../_types';

type Props = {
  featureBeingInstalled?: InstallableKentFeature['type'];
  kentFeatures?: InstallableKentFeature[];
  selectedFeatures: SelectedFeatures;
  onToggleFeature: (featureType: InstallableKentFeature['type']) => void;
  onToggleKentSuite: (shouldCheck: boolean) => void;
};

const ALL_INSTALLABLE_KENT_FEATURES_TYPES: InstallableKentFeature['type'][] = [
  'WORKLOAD_RIGHT_SIZING',
  'SPOT_INTERRUPTION_PREDICTION',
  'REBALANCING',
  'POD_MUTATIONS',
  'CLM',
];

const isKentSuiteChecked = ({
  selectedFeatures,
  featureBeingInstalled,
}: {
  selectedFeatures: SelectedFeatures;
  featureBeingInstalled?: InstallableKentFeature['type'];
}): boolean => {
  /*
    since feature being installed should be always checked and is hoisted out of the kent suite
    we need to filter it out
  */
  return Object.entries(selectedFeatures).some(
    ([featureType, checked]) => checked && featureType !== featureBeingInstalled
  );
};

const featureTypeToFeatureItemTestIdMap: Record<
  InstallableKentFeature['type'],
  string
> = {
  WORKLOAD_RIGHT_SIZING: 'workload-right-sizing-feature-item',
  SPOT_INTERRUPTION_PREDICTION: 'spot-interruption-prediction-feature-item',
  REBALANCING: 'rebalancer-feature-item',
  POD_MUTATIONS: 'pod-mutations-feature-item',
  CLM: 'clm-feature-item',
};

const isKentSuiteCheckboxIndeterminate = ({
  selectedFeatures,
  featureBeingInstalled,
}: {
  selectedFeatures: SelectedFeatures;
  featureBeingInstalled?: InstallableKentFeature['type'];
}): boolean => {
  /*
    since feature being installed should be always checked and is hoisted out of the kent suite
    we need to filter it out
  */
  const featureEntriesToConsider = Object.entries(selectedFeatures).filter(
    ([featureType]) => featureType !== featureBeingInstalled
  );

  const checkedFeaturesCount = featureEntriesToConsider.filter(
    ([_, checked]) => checked
  ).length;

  if (
    checkedFeaturesCount === 0 ||
    checkedFeaturesCount === featureEntriesToConsider.length
  ) {
    return false;
  }

  return true;
};

export const KentFeaturesSelection = ({
  featureBeingInstalled,
  kentFeatures = [],
  selectedFeatures,
  onToggleFeature,
  onToggleKentSuite,
}: Props) => {
  const [isExpanded, setExpanded] = useState(false);
  const featuresToDisplay = useMemo((): InstallableKentFeature['type'][] => {
    if (!featureBeingInstalled) {
      return ALL_INSTALLABLE_KENT_FEATURES_TYPES;
    }

    return ALL_INSTALLABLE_KENT_FEATURES_TYPES.filter(
      (featureType) => featureType !== featureBeingInstalled
    );
  }, [featureBeingInstalled]);

  const atLeastOneKentFeatureInstalled = useMemo(
    () => isAtLeastOneKentFeatureInstalled(kentFeatures),
    [kentFeatures]
  );

  const kentFeaturesMap = useMemo(
    () => getKentFeaturesMap(kentFeatures),
    [kentFeatures]
  );
  return (
    <Stack>
      <AgentSetupBlock
        testId="kent-suite-item"
        indeterminate={isKentSuiteCheckboxIndeterminate({
          selectedFeatures,
          featureBeingInstalled,
        })}
        checked={isKentSuiteChecked({
          selectedFeatures,
          featureBeingInstalled,
        })}
        heading="Karpenter Enterprise suite"
        description={`Add ${CAST_AI} automation capabilities to your existing Karpenter setup.`}
        icon={<Icons.CastCostOptimization variant="preset" weight="light" />}
        labelChip={<FeatureLabel type="premium" />}
        hasBeenInstalled={atLeastOneKentFeatureInstalled}
        onChange={onToggleKentSuite}
      />
      <Stack sx={{ pl: 32, mt: -8 }} gap={4}>
        <ToggleButton
          testId="kent-features-selection-toggle"
          title={`${featuresToDisplay.length} features`}
          isExpanded={isExpanded}
          onToggle={() => setExpanded((prev) => !prev)}
        />
        <Collapse in={isExpanded} unmountOnExit>
          <Stack data-testid="kent-features-selection-items">
            {featuresToDisplay.map((featureType) => (
              <KentFeatureItem
                key={featureType}
                testId={featureTypeToFeatureItemTestIdMap[featureType]}
                featureType={featureType}
                checked={selectedFeatures[featureType]}
                hasBeenInstalled={
                  !!kentFeaturesMap[featureType] &&
                  isKentFeatureInstalled(kentFeaturesMap[featureType])
                }
                onToggle={() => onToggleFeature(featureType)}
              />
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  );
};
